import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No reset token provided');
        setValidating(false);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/auth/validate-reset-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();
        
        if (response.ok && data.valid) {
          setTokenValid(true);
        } else {
          setError(data.error || 'Invalid or expired reset token');
        }
      } catch (err) {
        console.error('Token validation error:', err);
        setError('Failed to validate reset token');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password strength
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!/[A-Z]/.test(password)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }

    if (!/[a-z]/.test(password)) {
      setError('Password must contain at least one lowercase letter');
      return;
    }

    if (!/\d/.test(password)) {
      setError('Password must contain at least one number');
      return;
    }

    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      setError('Password must contain at least one special character');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword: password }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login', { state: { message: 'Password reset successful! Please log in with your new password.' } });
        }, 2000);
      } else {
        setError(data.error || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="page">
        <header>
          <img className="logo" src="/logo.png" alt="Stewart Valuation Intelligence" />
          <h1>Remote Inspection</h1>
        </header>

        <main className="main">
          <div className="card" style={{maxWidth: '600px', textAlign: 'center'}}>
            <h2>Validating Reset Token...</h2>
            <p style={{color: '#64748b'}}>Please wait</p>
          </div>
        </main>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="page">
        <header>
          <img className="logo" src="/logo.png" alt="Stewart Valuation Intelligence" />
          <h1>Remote Inspection</h1>
        </header>

        <main className="main">
          <div className="card" style={{maxWidth: '450px'}}>
            <div style={{textAlign: 'center', marginBottom: '24px'}}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>❌</div>
              <h2>Invalid Reset Link</h2>
            </div>

            <div style={{
              padding: '12px 16px',
              marginBottom: '16px',
              backgroundColor: '#fee2e2',
              border: '1px solid #ef4444',
              borderRadius: '8px',
              color: '#991b1b'
            }}>
              <strong>⚠️ Error:</strong> {error}
            </div>

            <p style={{marginBottom: '24px', color: '#64748b'}}>
              This password reset link is invalid or has expired. Please request a new password reset.
            </p>

            <div className="section">
              <Link to="/forgot-password">
                <button type="button" style={{width: '100%'}}>
                  Request New Reset Link
                </button>
              </Link>
            </div>

            <p className="hint" style={{textAlign: 'center', marginTop: '20px'}}>
              <Link to="/login" style={{color: '#2563eb', textDecoration: 'none', fontWeight: '500'}}>Back to Login</Link>
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (success) {
    return (
      <div className="page">
        <header>
          <img className="logo" src="/logo.png" alt="Stewart Valuation Intelligence" />
          <h1>Remote Inspection</h1>
        </header>

        <main className="main">
          <div className="card" style={{maxWidth: '600px'}}>
            <div style={{textAlign: 'center', marginBottom: '24px'}}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>✅</div>
              <h2>Password Reset Successful!</h2>
            </div>

            <p style={{marginBottom: '24px', color: '#64748b', textAlign: 'center'}}>
              Your password has been successfully reset. Redirecting to login...
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="page">
      <header>
        <img className="logo" src="/logo.png" alt="Stewart Valuation Intelligence" />
        <h1>Remote Inspection</h1>
      </header>

      <main className="main">
        <div className="card" style={{maxWidth: '600px'}}>
          <h2>Reset Your Password</h2>
          <p style={{marginBottom: '24px', color: '#64748b'}}>
            Enter your new password below.
          </p>

          <form onSubmit={handleSubmit}>
            <div className="section">
              <label htmlFor="password">New Password</label>
              <input
                id="password"
                type="password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                autoFocus
              />
              <div style={{
                marginTop: '8px',
                padding: '12px',
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#0c4a6e'
              }}>
                <strong>Password Requirements:</strong>
                <ul style={{margin: '8px 0 0 20px', paddingLeft: '0'}}>
                  <li style={{marginBottom: '4px'}}>Minimum 8 characters</li>
                  <li style={{marginBottom: '4px'}}>At least one uppercase letter (A-Z)</li>
                  <li style={{marginBottom: '4px'}}>At least one lowercase letter (a-z)</li>
                  <li style={{marginBottom: '4px'}}>At least one number (0-9)</li>
                  <li style={{marginBottom: '4px'}}>At least one special character (!@#$%^&*etc.)</li>
                </ul>
              </div>
            </div>

            <div className="section">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                id="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>

            {error && (
              <div style={{
                padding: '12px 16px',
                marginBottom: '16px',
                backgroundColor: '#fee2e2',
                border: '1px solid #ef4444',
                borderRadius: '8px',
                color: '#991b1b'
              }}>
                <strong>⚠️ Error:</strong> {error}
              </div>
            )}

            <div className="section">
              <button type="submit" disabled={loading}>
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

export default ResetPassword;

